import { useInfiniteQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { Fragment, useMemo, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import InfiniteLoader from '@/components/InfiniteLoader';
import { PageHeading, SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import SearchBar from '@/components/SearchBar';
import { Table, TableContent, Th } from '@/components/Table';
import Text from '@/components/Text';
import useDebounce from '@/hooks/useDebounce';
import { MemeResource, PaginatedResponse } from '@/types/resources';
import { api } from '@/utils/api';

import ImportModal from './components/ImportModal';
import TableRow from './components/TableRow';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 32px;

  ${PageHeading} {
    margin: 0;
  }
`;

const AnimatedMemes = () => {
  const [showImportModal, setShowImportModal] = useState(false);

  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search, 500);

  const fetchMemes = ({ pageParam = '/v1/memes' }) => {
    return api.get<never, PaginatedResponse<MemeResource>>(pageParam, {
      params: {
        ...(!!debouncedSearch && {
          'filter[search]': debouncedSearch,
        }),
      },
    });
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<MemeResource>>({
    queryKey: [
      'animated_memes',
      {
        ...(!!debouncedSearch && {
          search: debouncedSearch,
        }),
      },
    ],
    queryFn: fetchMemes,
    getNextPageParam: (lastPage) => {
      return lastPage.links.next;
    },
    refetchInterval: 20_000,
  });

  const memes = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<MemeResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        [],
      ),
    [data],
  );

  return (
    <>
      <Text $type="caption" $color="gray">
        BORED PANDA
      </Text>
      <Header>
        <PageHeading>Animated Memes</PageHeading>
        <Button onClick={() => setShowImportModal(true)}>Import CSV</Button>
      </Header>
      <SearchBar
        value={search}
        setValue={setSearch}
        placeholder="Search by Title"
      />
      <ScrollableTable>
        <Table $fixed>
          <thead>
            <tr>
              <Th>Meme</Th>
              <Th>Title</Th>
              <Th>Text</Th>
              <Th>Duration</Th>
              <Th>Status</Th>
              <Th>Owner</Th>
              <Th>Created At</Th>
              <Th>Error</Th>
              <Th $actionsCol>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!memes?.length}
              colSpan={7}
            >
              {memes?.map((meme) => <TableRow key={meme.id} meme={meme} />)}
            </TableContent>
          </tbody>
        </Table>
      </ScrollableTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
      <AnimatePresence>
        {showImportModal && (
          <ImportModal onClose={() => setShowImportModal(false)} />
        )}
      </AnimatePresence>
    </>
  );
};

export default AnimatedMemes;
